import React from 'react';
import { Box, TextField, Switch, FormControlLabel, Typography, Paper } from '@mui/material';

interface IWarrantyValue {
  value: number | null;
  enable: boolean;
  unit: string;
}

interface IWarrantyFieldProps {
  label: string;
  value: IWarrantyValue | null;
  onChange: (newValue: IWarrantyValue) => void;
  description?: string;
  disabled?: boolean;
  unit?: string; // Add unit prop to inject from schema
}

/**
 * Composant pour afficher et éditer un champ warranty avec toggle enable/disable.
 */
export const WarrantyField: React.FC<IWarrantyFieldProps> = ({
  label,
  value,
  onChange,
  description,
  disabled = false,
  unit = '', // Default to empty string
}) => {
  // Valeurs par défaut si value est null
  const currentValue = value || {
    value: null,
    enable: true,
    unit: unit, // Use unit from props
  };

  const handleEnableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...currentValue,
      enable: event.target.checked,
      unit: unit, // Ensure unit is always included
    });
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === '' ? null : parseFloat(event.target.value);
    onChange({
      ...currentValue,
      value: newValue,
      unit: unit, // Ensure unit is always included
    });
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Header avec toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant={"subtitle1"} fontWeight={"medium"}>
            {label}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={currentValue.enable}
                onChange={handleEnableChange}
                disabled={disabled}
                size={"small"}
              />
            }
            label={currentValue.enable ? 'Enabled' : 'Disabled'}
            labelPlacement={"start"}
          />
        </Box>

        {/* Description */}
        {description && (
          <Typography variant={"caption"} color={"text.secondary"}>
            {description}
          </Typography>
        )}

        {/* Input pour la valeur */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            type={"number"}
            value={currentValue.value ?? ''}
            onChange={handleValueChange}
            disabled={disabled || !currentValue.enable}
            size={"small"}
            fullWidth
            placeholder={"Enter value"}
            inputProps={{
              step: 0.01,
            }}
          />
          {unit && (
            <Typography
              variant={"body2"}
              sx={{
                minWidth: '120px',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
